import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { BreadService } from '../bread.service';
import { BaseCrudService } from '../bread-crud.service';
import { ApiService } from '../api.service';
import { LoaderService } from '../loader.service';

export interface Invoice {
  id?: number;
  invoice_ref?: string;
  invoice_number?: number;
  invoice_type_id?: number;
  invoice_status_id?: number;
  client_id?: number;
  client?: any;
  company_id?: number;
  company?: any;
  shop_id?: number;
  shop?: any;
  order_id?: number;
  parent_id?: number;
  invoice_date?: string;
  invoice_date_limit?: string;
  net_price?: number;
  total_tax?: number;
  reduction_amount?: number;
  gross_price?: number;
  currency?: string;
  payment_status_id?: number;
  pdf_url?: string;
  /** PDF acquitté (facture soldée) — distinct de pdf_url, qui reste la pièce émise. */
  paid_pdf_url?: string | null;
  /** Acheteur sans compte (facture rattachée au client de repli) : identité de la commande. */
  billing_identity?: { firstname?: string | null; lastname?: string | null; email?: string | null; phone?: string | null; address?: string | null } | null;
  uuid_public?: string;
  products?: any[];
  type?: any;
  status?: any;
  /**
   * Documents créés à partir de celui-ci (relation Eloquent `children`) — sert
   * aussi bien aux factures d'acompte qu'aux factures issues d'un devis ou
   * d'une récurrence. Le TYPE (`child.type?.name`) est le seul discriminant
   * valable pour repérer un acompte, jamais `parent_id` seul.
   */
  children?: Invoice[];
  created_at?: string;
  updated_at?: string;
  custom_attributes?: any;
}

@Injectable({ providedIn: 'root' })
export class InvoiceService extends BaseCrudService<Invoice> {
  protected model = 'invoices';
  private api = inject(ApiService);
  private loader = inject(LoaderService);

  constructor(bread: BreadService) {
    super(bread);
  }

  /**
   * Send invoice by email
   */
  send(id: string | number, payload: { to?: string; cc?: string; bcc?: string; subject?: string; message?: string; link?: string }): Observable<any> {
    this.loader.startLoading();
    return this.api.post<{ data: any }>(`kreosi/invoices/send-email/${id}`, payload)
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error sending invoice:', err);
          return throwError(() => err);
        }),
        finalize(() => this.loader.stopLoading())
      );
  }

  /**
   * Adresses à pré-remplir dans la fenêtre d'envoi : les mêmes que celles des
   * relances et de la confirmation de paiement (le caser peut en ajouter).
   */
  recipients(id: string | number): Observable<string[]> {
    return this.api.get<any>(`kreosi/read/invoices/${id}/recipients`).pipe(
      map(res => ((res as any)?.data?.emails ?? []) as string[]),
      catchError(() => of([] as string[]))
    );
  }

  /**
   * Pièces jointes (justificatifs) d'une pièce : devis, facture, avoir.
   * Distinct du document légal (`pdf_url`). On passe l'uuid_public, jamais l'id numérique.
   */
  getAttachments(idOrUuid: string | number): Observable<any> {
    return this.api.get<{ data: any }>(`kreosi/read/invoices/${idOrUuid}/attachments`)
      .pipe(map(res => (res as any).data ?? res));
  }

  uploadAttachments(idOrUuid: string | number, files: File[]): Observable<any> {
    const formData = new FormData();
    files.forEach(f => formData.append('files[]', f));
    return this.api.postFormData<{ data: any }>(`kreosi/edit/invoices/${idOrUuid}/attachments`, formData)
      .pipe(map(res => (res as any).data ?? res));
  }

  deleteAttachment(idOrUuid: string | number, attachmentUuid: string | number): Observable<any> {
    return this.api.delete(`kreosi/edit/invoices/${idOrUuid}/attachments/${attachmentUuid}`);
  }

  /**
   * Duplique la facture (même type) : renvoie le nouveau document (Brouillon).
   */
  duplicate(id: string | number): Observable<any> {
    this.loader.startLoading();
    return this.api.post<{ data: any }>(`kreosi/invoices/duplicate/${id}`, {})
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error duplicating invoice:', err);
          return throwError(() => err);
        }),
        finalize(() => this.loader.stopLoading())
      );
  }

  /**
   * Send reminder email for invoice
   */
  sendReminder(id: string | number): Observable<any> {
    return this.api.post<{ data: any }>(`kreosi/invoices/send-reminder-email/${id}`, {})
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error sending reminder:', err);
          throw err;
        })
      );
  }

  /**
   * Situation des factures (LOGISTIKI) : règlements, réglé et reste dû par facture,
   * totaux de l'année.
   */
  situation(params: InvoiceSituationParams): Observable<InvoiceSituationResponse> {
    const query: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined && value !== '') {
        query[key] = value as string | number;
      }
    }
    return this.api.get<InvoiceSituationResponse>('kreosi/invoices/situation', { params: query });
  }

  /** Journal des encaissements (LOGISTIKI) : règlements des factures et des commandes. */
  encaissements(params: PaymentJournalParams): Observable<PaymentJournalResponse> {
    const query: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined && value !== '') {
        query[key] = value as string | number;
      }
    }
    return this.api.get<PaymentJournalResponse>('kreosi/invoices/encaissements', { params: query });
  }

  /**
   * Modifier un encaissement déjà enregistré sur une facture.
   * L'API refuse (400) de changer le montant d'un paiement en ligne ou d'une
   * facture à échéancier ; date, mode et référence restent modifiables.
   */
  updatePayment(invoiceId: string | number, paymentId: number, payload: {
    given: number;
    change: number;
    payment_method_id: number;
    payment_date: string;
    check_number?: string | null;
  }): Observable<any> {
    return this.api.post<{ data: any }>(`kreosi/invoices/${invoiceId}/payments/${paymentId}`, payload)
      .pipe(map(res => (res as any).data || res));
  }

  /**
   * Add a payment to an invoice
   */
  addPayment(id: string | number, payload: {
    total: number;
    given: number;
    change: number;
    payment_method_id: number;
    payment_date: string;
    // Référence du règlement (n° de chèque, de virement…).
    check_number?: string | null;
    // Ids des échéances à solder par ce versement ; absente => FIFO côté API,
    // présente mais vide => aucune échéance soldée.
    installment_ids?: number[];
  }): Observable<any> {
    return this.api.post<{ data: any }>(`kreosi/invoices/add-payment/${id}`, payload)
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error adding invoice payment:', err);
          throw err;
        })
      );
  }

  /**
   * Définit / remplace l'échéancier d'un paiement en plusieurs fois pour une facture.
   * Body: { schedule: [ { due_date: 'YYYY-MM-DD', amount?: number, rate?: number, label?: string } ] }
   * La somme des échéances doit égaler le total TTC (la dernière est ajustée côté API).
   */
  setInstallmentPlan(id: string | number, payload: {
    schedule: Array<{ due_date: string; amount?: number; rate?: number; label?: string }>;
  }): Observable<any> {
    return this.api.post<{ data: any }>(`kreosi/invoices/${id}/installment-plan`, payload)
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error setting installment plan:', err);
          return throwError(() => err);
        })
      );
  }

  /**
   * Get invoice by UUID (public endpoint)
   */
  getByUuid(uuid: string): Observable<Invoice> {
    return this.api.fetch<Invoice>(`kreosi/invoices/preview/${uuid}`, {
      prefix: '',
      paginate: false
    })
    .pipe(
      map(res => (res as any).data || res),
      catchError(err => {
        console.error('Error fetching invoice:', err);
        return of(null as any);
      })
    );
  }

  /**
   * (Ré)génère la facture acquittée d'une facture payée. `null` si l'API refuse
   * (facture non payée) ou échoue.
   */
  generatePaidPdf(id: string | number): Observable<any> {
    return this.api.fetch<any>(`kreosi/invoices/generate-paid-pdf/${id}`, {
      prefix: '',
      paginate: false
    })
    .pipe(
      map(res => (res as any).data || res),
      catchError(err => {
        console.error('Error generating paid PDF:', err);
        return of(null as any);
      })
    );
  }

  /**
   * Get invoice by UUID (public endpoint)
   */
  generatePdf(id: string | number): Observable<string> {
    return this.api.fetch<any>(`kreosi/invoices/generate-pdf/${id}`, {
      prefix: '',
      paginate: false
    })
    .pipe(
      map(res => (res as any).data || res),
      catchError(err => {
        console.error('Error generating PDF:', err);
        return of(null as any);
      })
    );
  }

  /**
   * Initie un paiement en ligne (PayZen) pour une facture publique (par uuid).
   * Le backend renvoie { status, fields, action_url } à soumettre à la passerelle.
   */
  initiateOnlinePayment(uuid: string): Observable<{ status: string; fields: Record<string, string>; action_url: string }> {
    return this.api.post<any>(`kreosi/invoices/${uuid}/initiate-payment`, {})
      .pipe(
        map(res => (res as any)),
        catchError(err => {
          console.error('Error initiating online payment:', err);
          return throwError(() => err);
        })
      );
  }

  /**
   * Construit un formulaire POST caché et le soumet vers la passerelle de paiement
   * (redirige le navigateur vers PayZen). Mirroir du flux checkout EEZILI.
   */
  submitPaymentForm(fields: Record<string, string>, actionUrl: string): void {
    if (typeof document === 'undefined') return;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = actionUrl;
    for (const [name, value] of Object.entries(fields || {})) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value as string;
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
  }
}

export type InvoiceSituationState = 'impayee' | 'partielle' | 'soldee' | 'trop_percu';

export interface InvoiceSituationParams {
  year?: number | null;
  state?: InvoiceSituationState | null;
  search?: string | null;
  shop_id?: number | null;
  page?: number;
  per_page?: number;
}

export interface InvoiceSituationPayment {
  id: number;
  date: string | null;
  method: string | null;
  reference: string | null;
  amount: number;
}

export interface InvoiceSituationRow {
  id: number;
  uuid_public: string | null;
  invoice_ref: string | null;
  title: string | null;
  description: string | null;
  invoice_date: string | null;
  currency: string;
  client_name: string | null;
  company_name: string | null;
  status: { name: string; color: string | null; icon: string | null } | null;
  gross_price: number;
  paid: number;
  remaining: number;
  state: InvoiceSituationState;
  payments: InvoiceSituationPayment[];
}

export interface InvoiceSituationResponse {
  data: InvoiceSituationRow[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    max_payments: number;
    totals: { count: number; invoiced: number; collected: number; unpaid: number };
    years: number[];
  };
}

export type PaymentJournalSource = 'facture' | 'commande';

export interface PaymentJournalParams {
  from?: string | null;
  to?: string | null;
  payment_method_id?: number | null;
  source?: PaymentJournalSource | null;
  search?: string | null;
  shop_id?: number | null;
  page?: number;
  per_page?: number;
}

export interface PaymentJournalRow {
  id: number;
  payment_date: string | null;
  given: number;
  change: number;
  /** Montant encaissé = reçu − rendu. */
  amount: number;
  currency: string;
  payment_method_id: number | null;
  method: string | null;
  check_number: string | null;
  online: boolean;
  /** Montant non modifiable (paiement en ligne ou facture à échéancier). */
  amount_locked: boolean;
  installments: boolean;
  source: PaymentJournalSource;
  invoice: { id: number; uuid_public: string | null; invoice_ref: string | null } | null;
  order: { id: number; uuid_public: string | null } | null;
  client_name: string | null;
  company_name: string | null;
}

export interface PaymentJournalResponse {
  data: PaymentJournalRow[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    totals: {
      count: number;
      amount: number;
      by_method: { payment_method_id: number | null; name: string; count: number; amount: number }[];
    };
  };
}
